import type { AccountInfo } from "@azure/msal-browser";

export const MSAL_SESSION_HINT_KEY = "app_msal_session_hint";

export type StoredMsalSessionHint = {
    username: string;
    updatedAt: number;
};

function getLocalStorage(): Storage | null {
    if (typeof window === "undefined") {
        return null;
    }

    return window.localStorage;
}

function parseStoredMsalSessionHint(
    storage: Storage | null
): StoredMsalSessionHint | null {
    if (!storage) {
        return null;
    }

    try {
        const rawHint = storage.getItem(MSAL_SESSION_HINT_KEY);
        if (!rawHint) {
            return null;
        }

        const parsedHint = JSON.parse(
            rawHint
        ) as Partial<StoredMsalSessionHint>;
        if (
            !parsedHint ||
            typeof parsedHint.username !== "string" ||
            !parsedHint.username.trim()
        ) {
            return null;
        }

        return {
            username: parsedHint.username,
            updatedAt:
                typeof parsedHint.updatedAt === "number" &&
                Number.isFinite(parsedHint.updatedAt)
                    ? parsedHint.updatedAt
                    : Date.now(),
        };
    } catch {
        return null;
    }
}

export function getStoredMsalSessionHint(): StoredMsalSessionHint | null {
    return parseStoredMsalSessionHint(getLocalStorage());
}

export function hasStoredMsalSessionHint(
    storage: Storage | null = getLocalStorage()
): boolean {
    return Boolean(parseStoredMsalSessionHint(storage));
}

export function setStoredMsalSessionHint(
    account:
        | Pick<AccountInfo, "username">
        | {
              username?: string | null;
          }
        | null
        | undefined
): void {
    const storage = getLocalStorage();
    if (!storage) {
        return;
    }

    const username =
        typeof account?.username === "string" ? account.username.trim() : "";

    if (!username) {
        storage.removeItem(MSAL_SESSION_HINT_KEY);
        return;
    }

    storage.setItem(
        MSAL_SESSION_HINT_KEY,
        JSON.stringify({
            username,
            updatedAt: Date.now(),
        } satisfies StoredMsalSessionHint)
    );
}

export function clearStoredMsalSessionHint(): void {
    getLocalStorage()?.removeItem(MSAL_SESSION_HINT_KEY);
}

export function hasMsalCacheArtifacts(
    storage: Storage | null = getLocalStorage()
): boolean {
    if (!storage) {
        return false;
    }

    for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith("msal.")) {
            return true;
        }
    }

    return false;
}

export function clearMsalCacheArtifacts(
    storage: Storage | null = getLocalStorage()
): void {
    if (!storage) {
        return;
    }

    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith("msal.")) {
            keysToRemove.push(key);
        }
    }

    keysToRemove.forEach((key) => storage.removeItem(key));
}

export function hasRecoverableMsalLocalState(
    storage: Storage | null = getLocalStorage()
): boolean {
    return hasStoredMsalSessionHint(storage) || hasMsalCacheArtifacts(storage);
}
